import { BackArrowIcon } from '@/components/atoms/BackArrowIcon';
import { HeadsetIcon } from '@/components/atoms/icons/HeadsetIcon';
import { Typography } from '@/components/atoms/Typography';
import { UIColorTokens } from '@/core/constants/uiColors';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { openSupportPhoneDialer } from '@/core/utils/phoneUtils';
import { useNavigation, usePathname } from 'expo-router';
import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { BaseHeader } from './BaseHeader';

export interface TextHeaderProps {
    title: string;
    onBackPress?: (() => void) | undefined;
    showSupportButton?: boolean;
    onSupportPress?: (() => void) | undefined;
    disableBack?: boolean; // externally disable
    hideBackIfRoot?: boolean; // auto-hide when cannot go back
    hideSupportButton?: boolean; // hide support button even if showSupportButton is true
    backButtonColor?: keyof UIColorTokens;
    supportButtonColor?: keyof UIColorTokens;
}

export const TextHeader = React.memo<TextHeaderProps>(function TextHeader({
    title,
    onBackPress,
    showSupportButton = false,
    onSupportPress,
    disableBack = false,
    hideBackIfRoot = true,
    hideSupportButton = false,
    backButtonColor = 'fontPrimary',
    supportButtonColor = 'fontPrimary',
}) {
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    const navigation = useNavigation();
    const pathname = usePathname();

    // Check if we can go back
    const isRoot = !pathname || pathname === '/';
    const navCanGoBack = typeof (navigation as any)?.canGoBack === 'function' ? (navigation as any).canGoBack() : false;

    // Default back handler using navigation. Re-checked at press time, not with
    // the render-time navCanGoBack: a quick second tap lands while this screen
    // is already popping, and goBack() from its (now removed) route throws
    // "The action 'GO_BACK' was not handled by any navigator". isFocused() is
    // false once the first goBack has run, so the repeat tap is ignored.
    const handleDefaultBack = () => {
        const nav = navigation as any;
        if (!nav?.goBack || nav.isFocused?.() === false || !nav.canGoBack?.()) {
            return;
        }
        nav.goBack();
    };

    // Determine if back button should show
    const shouldShowBack = !disableBack && (
        onBackPress || // Always show if custom handler provided
        (!hideBackIfRoot || (!isRoot && navCanGoBack)) // Show if not hiding on root or can actually go back
    );

    // Use custom handler if provided, otherwise use default navigation
    const backHandler = onBackPress || handleDefaultBack;

    return (
        <BaseHeader
            leading={
                shouldShowBack ? (
                    <TouchableOpacity onPress={backHandler} style={styles.buttonContainer}>
                        {/* BackArrowIcon already handles RTL internally, no need for extra transform */}
                        <BackArrowIcon
                            size={24}
                            color={colors[backButtonColor]}
                        />
                    </TouchableOpacity>
                ) : (
                    <View style={styles.placeholder} />
                )
            }
            middle={
                <View style={styles.titleContainer}>
                    <Typography 
                        variant="title3" 
                        color="primary" 
                        style={[styles.title, { textAlign: isRTL ? 'right' : 'center' }]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                    >
                        {title}
                    </Typography>
                </View>
            }
            trailing={
                showSupportButton && !hideSupportButton ? (
                    <TouchableOpacity
                        onPress={onSupportPress || openSupportPhoneDialer}
                        style={styles.supportButton}
                    >
                        <HeadsetIcon size={24} color={colors[supportButtonColor]} />
                    </TouchableOpacity>
                ) : (
                    <View style={styles.placeholder} />
                )
            }
        />
    );
});

const styles = StyleSheet.create({
    buttonContainer: {
        height: 40,
        width: 40, // Fixed width for back button
        justifyContent: 'center',
    },
    supportButton: {
        height: 40,
        width: 40, // Fixed width for support button
        alignItems: 'center',
        justifyContent: 'center',
    },
    placeholder: {
        height: 40,
        width: 40, // Same fixed width as other elements
    },
    titleContainer: {
        width: '100%', // Take full width of centerSection
        minWidth: 0, // Allow text to shrink below content size
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 8, // Add padding to prevent text from touching edges
    },
    title: {
        textAlign: 'center',
        lineHeight: 24, // Ensure proper line height for title3
    },
});

export default TextHeader;


